/**
 * Consent state for analytics.
 *
 * Three states only: 'granted' | 'denied' | 'unset'.
 * Stored in localStorage under a single key. No cookies, no sync to server.
 */

export type Consent = "granted" | "denied" | "unset";

const STORAGE_KEY = "hk_analytics_consent_v1";
const EVENT_NAME = "hk:analytics-consent-change";

type Listener = (c: Consent) => void;

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

export function getConsent(): Consent {
  if (!isBrowser()) return "unset";
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "granted" || v === "denied") return v;
    return "unset";
  } catch {
    return "unset";
  }
}

export function setConsent(next: Consent): void {
  if (!isBrowser()) return;
  try {
    if (next === "unset") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, next);
    }
  } catch {
    /* noop */
  }
  // Browser events are the single notification channel. Unlike a module-level
  // listener registry, this remains shared if Vite/HMR or code splitting loads
  // more than one instance of this module.
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
    }
  } catch {
    /* noop */
  }
}

export function subscribeConsent(fn: Listener): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<Consent>).detail;
    if (detail === "granted" || detail === "denied" || detail === "unset") {
      try {
        fn(detail);
      } catch {
        /* noop */
      }
    }
  };
  const storageHandler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) fn(getConsent());
  };
  if (typeof window !== "undefined") {
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", storageHandler);
  }
  return () => {
    if (typeof window !== "undefined") {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", storageHandler);
    }
  };
}